import { useMemo } from 'react';
import { Typography } from 'src/components/common';

import { keyLabelMapping } from './keyLabelMapping';
import './IdeaDetailsSectionView.scss';

import IdeaLabelValue, { TIdeaLabelValue } from '../ideaLabelValue/IdeaLabelValue';

export type TIdeaDetailsSectionViewProps = {
  sectionTitle: string;
  data: Record<string, any>;
};

const IdeaDetailsSectionView = ({ sectionTitle, data }: TIdeaDetailsSectionViewProps) => {
  if (data === null || data === undefined) {
    console.log('IdeaDetailsSectionView', data);
    data = { validationReport: 'No report found' };
  }

  const fields: TIdeaLabelValue[] = useMemo(() => {
    try {
      const keys: string[] = Object.keys(data);
      return keys.map((key) => {
        const label: string = keyLabelMapping[key as keyof typeof keyLabelMapping] || '';
        let value: string = data[key as keyof typeof data];

        // Handle JSON format - check if it's a structured validation report
        if (key === 'validationReport' && typeof value === 'string') {
          try {
            const parsedValue = JSON.parse(value);
            // Check if it's the new JSON format with specific keys
            if (parsedValue && typeof parsedValue === 'object' &&
              ('overallFeedback' in parsedValue || 'marketFeasibility' in parsedValue)) {
              // Check if it's the "no input" response
              const isNoInputResponse = Object.values(parsedValue).every(
                (val: any) => val === "Please provide appropriate input to generate an actionable report."
              );

              if (isNoInputResponse) {
                value = "Please provide appropriate input to generate an actionable report.";
              } else {
                // Convert JSON structure to markdown-like format
                value = convertJsonToMarkdown(parsedValue);
              }
            }
          } catch (e) {
            // If parsing fails, keep the original value (it's probably already markdown)
          }
        }
        return { label, value };
      });
    } catch {
      return [];
    }
  }, [JSON.stringify(data), sectionTitle]);

  return (
    <div className="idea-details-section-view">
      <div className="idea-details-section-view__header">
        <Typography variant="subHeading-2" fontWeight="semiBold">
          {sectionTitle}
        </Typography>
      </div>
      <div className="idea-details-section-view__content">
        {fields.map((field, index) => {
          return <IdeaLabelValue key={index} label={field.label} value={field.value} />;
        })}
      </div>
    </div>
  );
};

// Helper function to convert JSON format to markdown format
const convertJsonToMarkdown = (jsonData: Record<string, string>): string => {
  const sections = [
    { key: 'overallFeedback', title: 'Overall Feedback' },
    { key: 'marketFeasibility', title: '1. Market Feasibility' },
    { key: 'idealCustomerAndProfile', title: '2. Ideal Customer and Profile' },
    { key: 'competitiveAnalysis', title: '3. Competitive Analysis' },
    { key: 'marketSizeAndGrowthPotential', title: '4. Market Size and Growth Potential' },
    { key: 'goToMarketStrategy', title: '5. Go-to-Market Strategy' },
    { key: 'risksAndMitigationStrategies', title: '6. Risks and Mitigation Strategies' },
    { key: 'financialViability', title: '7. Financial Viability' },
    { key: 'oneHighValueRecommendation', title: '8. One High-Value Recommendation' }
  ];

  let markdownContent = '';

  sections.forEach((section) => {
    if (jsonData[section.key]) {
      markdownContent += `**${section.title}:**\n${jsonData[section.key]}\n\n`;
    }
  });

  return markdownContent.trim();
};

export default IdeaDetailsSectionView;
